/**
 * Team → Daily update (founder, AD) and My day → Done today (a member) — prototype VIEWS.daily (docs/prototype.html
 * L2425): "Preview the message" in the head; four stats (hidden in a hub by the prototype CSS, as there); for managers
 * the Person dropdown (Everyone, or any set of people; t-da3a924a) and the "Nothing logged yet from …" note; then one panel per person:
 * done today grouped by client (most important client first, Internal last) with the Proof chip and the time, then
 * "Moved or left out", then "Still overdue".
 *
 * Data: GET /team/daily (today, IST). Managers get everyone (one unpaged list) and filter on the page; a member's call
 * returns only themselves (the server scopes it). The message preview is composed on the page from the same payload.
 * ADR-001 §10 defers outbound email, so the preview is for copying; nothing is sent on a timer in this version.
 */
import { useState } from 'react';
import type { DailyDone, DailyDoneKind, DailyOtherKind, DailyPerson, Priority, TeamDailyResponse } from '@amplispot/contracts';
import { dayLabel, firstName } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useTeamDaily } from '../../lib/team';
import { Avatar, Empty, ErrorState, Loading, Modal, MultiSelectFilter, useToast } from '../../ui/kit';
import { TaskPanel } from '../work/TaskPanel';
import { Stat } from '../timesheets/TimesheetReport';
import { COPY_FAILED, copyText } from '../../lib/clipboard';

/** Prototype KINDW. */
const KINDW: Record<DailyDoneKind | DailyOtherKind, string> = {
  closed: 'Closed a task',
  routine: 'Routine done',
  meeting: 'Logged a meeting',
  client_task: 'Closed a client task',
  esc: 'Closed an escalation',
  request: 'Request done',
  pushed: 'Moved a date',
  dropped: 'Left out a task',
  created: 'Tasks created',
};
const PRIO: Record<Priority, number> = { high: 0, med: 1, low: 2 };

/** "17:40" in India time. */
const time = (ts: string) => new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' }).format(new Date(ts));

/** Prototype duGroup: by client, highest priority client first, then name; internal work last. */
export function groupByClient(list: DailyDone[]): [DailyDone['client'], DailyDone[]][] {
  const m = new Map<string, [DailyDone['client'], DailyDone[]]>();
  for (const a of list) {
    const k = a.client?.id ?? '_';
    if (!m.has(k)) m.set(k, [a.client, []]);
    m.get(k)![1].push(a);
  }
  return [...m.values()].sort(([a], [b]) => (!a ? 1 : !b ? -1 : PRIO[a.priority] - PRIO[b.priority] || a.name.localeCompare(b.name)));
}

/** Prototype duText. */
export function dailyText(d: TeamDailyResponse, people: DailyPerson[]): string {
  const L = [`Daily update, ${dayLabel(d.date)}`, ''];
  for (const p of people) {
    L.push(`${p.user.name}: ${p.done.length} done, ${p.overdue.length} overdue, ${p.due_next_day} due ${dayLabel(d.next_day)}`);
    for (const [c, l] of groupByClient(p.done)) for (const a of l) L.push(`  - ${c ? c.name + ': ' : ''}${a.text}${a.has_proof ? ' (proof attached)' : ''}`);
    for (const a of p.other) L.push(`  * ${KINDW[a.kind]}: ${a.text}`);
    if (!p.done.length) L.push('  Nothing logged as done today.');
    L.push('');
  }
  if (d.totals.left_out) L.push(`Left out today: ${d.totals.left_out}`);
  return L.join('\n').trimEnd();
}

export function DailyUpdate() {
  const { can } = useSession();
  const lead = can('team.read');
  const q = useTeamDaily({});
  const toast = useToast();
  /** Picked people; empty = Everyone. On screen over the whole team (GET /team/daily is not paged). */
  const [who, setWho] = useState<string[]>([]);
  const [preview, setPreview] = useState(false);
  const [task, setTask] = useState<string | null>(null);

  if (q.isPending) return <Loading label="Loading what was done today…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;
  const all = !lead || !who.length;
  const ps = all ? d.people : d.people.filter((p) => who.includes(p.user.id));
  const sum = (f: (p: DailyPerson) => number) => ps.reduce((n, p) => n + f(p), 0);
  const quiet = !lead ? [] : all ? d.quiet : d.quiet.filter((u) => who.includes(u.id));
  const text = dailyText(d, ps);

  return (
    <>
      <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
        <div className="r" style={{ marginLeft: 0 }}>
          <button className="btn" onClick={() => setPreview(true)}>
            Preview the message
          </button>
        </div>
      </div>
      <div className="stats">
        <Stat k="Done today" v={sum((p) => p.done.length)} s="tasks, routine, meetings" cls="green" />
        <Stat k="Dates moved" v={sum((p) => p.other.filter((a) => a.kind === 'pushed').length)} cls="amber" />
        <Stat k="Left out" v={all ? d.totals.left_out : sum((p) => p.other.filter((a) => a.kind === 'dropped').length)} s="tasks turned down" />
        <Stat k="Overdue now" v={sum((p) => p.overdue.length)} s="still open" cls="red" />
      </div>
      {lead ? (
        <div className="bar">
          <MultiSelectFilter
            label="Person"
            allLabel="Everyone"
            options={d.people.map((p) => ({ value: p.user.id, label: p.user.name, count: `${p.done.length} done` }))}
            value={who}
            onChange={setWho}
          />
        </div>
      ) : null}
      {quiet.length ? (
        <div className="note warn" style={{ marginBottom: 14 }}>
          <b>Nothing logged yet from {quiet.map((u) => firstName(u.name)).join(', ')}.</b> They may still be working.
        </div>
      ) : null}
      <div style={{ display: 'grid', gap: 16 }}>
        {ps.map((p) => (
          <div className="panel" key={p.user.id}>
            <div className="hd">
              <h3 className="split" style={{ gap: 8 }}>
                <Avatar user={p.user} /> {p.user.name}
              </h3>
              <div className="r">
                <span className={`chip ${p.done.length ? 'hot' : 'cold'}`}>{p.done.length} done</span>
                {p.overdue.length ? <span className="chip cold">{p.overdue.length} overdue</span> : null}
                {p.due_next_day ? (
                  <span className="chip warm" title={`Due ${dayLabel(d.next_day)}, the next working day`}>
                    {p.due_next_day} due tomorrow
                  </span>
                ) : null}
              </div>
            </div>
            {p.done.length ? (
              groupByClient(p.done).map(([c, l]) => (
                <div key={c?.id ?? '_'}>
                  <div className="lbl" style={{ padding: '10px 16px 2px' }}>
                    {c ? c.name : 'Internal'}
                  </div>
                  {l.map((a) => (
                    <div className={`li${a.task_id ? ' click' : ''}`} key={a.id} onClick={a.task_id ? () => setTask(a.task_id) : undefined}>
                      <div className="b">
                        <div className="t" style={{ fontWeight: 500 }}>
                          {a.text}
                        </div>
                        <div className="m">
                          <span>{KINDW[a.kind]}</span>
                        </div>
                      </div>
                      {a.has_proof ? <span className="chip hot">Proof</span> : null}
                      <span className="sub" style={{ color: 'var(--sub)', fontSize: 12 }}>
                        {time(a.at)}
                      </span>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="empty">
                <b>Nothing logged as done today</b>
              </div>
            )}
            {p.other.length ? (
              <>
                <div className="lbl" style={{ padding: '10px 16px 2px' }}>
                  Moved or left out
                </div>
                {p.other.map((a) => (
                  <div className={`li${a.task_id ? ' click' : ''}`} key={a.id} onClick={a.task_id ? () => setTask(a.task_id) : undefined}>
                    <div className="b">
                      <div className="t" style={{ fontWeight: 500 }}>
                        {a.text}
                      </div>
                      <div className="m">
                        <span>{KINDW[a.kind]}</span>
                      </div>
                    </div>
                    <span className="sub" style={{ color: 'var(--sub)', fontSize: 12 }}>
                      {time(a.at)}
                    </span>
                  </div>
                ))}
              </>
            ) : null}
            {p.overdue.length ? (
              <>
                <div className="lbl" style={{ padding: '10px 16px 2px' }}>
                  Still overdue
                </div>
                {p.overdue.map((t) => (
                  <div className="li click" key={t.task_id} onClick={() => setTask(t.task_id)}>
                    <div className="b">
                      <div className="t" style={{ fontWeight: 500 }}>
                        {t.title}
                      </div>
                      <div className="m">
                        <span>{t.client ? t.client.name : 'Internal'}</span>
                      </div>
                    </div>
                    <span className="chip cold">Overdue {t.days_overdue}d</span>
                  </div>
                ))}
              </>
            ) : null}
          </div>
        ))}
        {!ps.length ? (
          <div className="panel">
            <Empty title="No one to show">There is no one in ops yet.</Empty>
          </div>
        ) : null}
      </div>
      {preview ? (
        <Modal
          title="Daily update message"
          sub={`${lead ? 'The whole team, or the people picked above.' : 'Your own day.'} Copy it to send it. It is not sent on a timer in this version.`}
          onClose={() => setPreview(false)}
          footer={
            <>
              <button
                className="btn"
                onClick={() => {
                  copyText(text, () => toast('Copied'), () => toast(COPY_FAILED));
                }}
              >
                Copy
              </button>
              <div className="grow" />
              <button className="btn" onClick={() => setPreview(false)}>
                Close
              </button>
            </>
          }
        >
          <textarea className="in" rows={14} style={{ fontFamily: 'var(--mono, monospace)', fontSize: 12.5 }} readOnly value={text} aria-label="Daily update message" />
        </Modal>
      ) : null}
      {task ? <TaskPanel id={task} onClose={() => setTask(null)} /> : null}
    </>
  );
}
